/**
 * The quiet foot that holds the provenance slot, on a set or on a detail.
 *
 * It is chrome and nothing else: the border, the tint, and one child. What goes
 * inside is `CresultcardProvenance`, whose own contract splits what a reader
 * needs (an as-of, a page bound, a licence) from the vendor's internal handles,
 * and keeps the handles inside a disclosure.
 *
 * That split is the whole reason this exists at set level too. Lane names,
 * credit counts, capture counts, next-offsets and attribution strings are facts
 * the projection genuinely carries, and they may not simply vanish — but they
 * are also not what a reader came for. A collapsed disclosure states them
 * without spending a line of the reader's attention on them, which an
 * always-visible rail could not do.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { CRC_DIVIDER } from "./cresultcard.tokens";

const RAIL_PAD_BLOCK = 8;
const RAIL_PAD_INLINE = 13;
const RAIL_GROUND = "rgba(0,0,0,0.2)";

interface CresultcardProvenanceRailProps {
  readonly children: ReactNode;
}

export function CresultcardProvenanceRail({
  children,
}: CresultcardProvenanceRailProps): ReactElement {
  return (
    <footer
      data-el="Cresultcard/ProvenanceRail/Root"
      style={{
        // The tray's field grid is an absolute overlay drawn before this
        // subtree, so a static foot would paint under it.
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        minWidth: 0,
        paddingBlock: RAIL_PAD_BLOCK,
        paddingInline: RAIL_PAD_INLINE,
        borderTop: `1px solid ${CRC_DIVIDER}`,
        backgroundColor: RAIL_GROUND,
      }}
    >
      {children}
    </footer>
  );
}
